import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Menu, MenuItem } from '@mui/material';
import { connect, useDispatch } from 'react-redux';
import _ from 'lodash';
import LabelValueDisplay from '../../../components/LabelValue/LabelValueDisplay';
import AddEditInterfaceModal from '../../../components/Modals/AddEditInterfaceModal';
import DeleteWANProfileModal from '../../../components/Modals/DeleteProfileModal';
import NetworkCardContainer from '../../../components/NetworkCards/NetworkCard';
import CustomLoader from '../../../../../../components/Loader/CustomLoader';
import NetworkTable, { ColumnConfig, ColumnType } from '../../../components/InterfacesTable/NetworkTable/NetworkTable';
import {
  getAllPolicyRoutesData,
  getAddEditPolicyRouteData,
  getEnableDisablePolicyRouteData,
  getDeletePolicyRouteData,
  getAllVpnProfilesData,
  getPolicyRouteFlowCountsData,
} from '../../../../../../actions/Users/authenticateOpenEdgeService';
import {
  GET_ADD_EDIT_POLICY_ROUTE,
  GET_ENABLE_DISABLE_POLICY_ROUTE,
  GET_DELETE_POLICY_ROUTE,
} from '../../../../../../actions/types';
import { getDataFromLocalStorage } from '../../../../../../utils/util';


interface Props {
  selectedRow?: any;
  refreshIconClicked?: any;
  notifyConfigAction?: () => void;
  notifyConfigActionComplete?: () => void;
  getAllPolicyRoutesData: (deviceId: string) => void;
  getAddEditPolicyRouteData: (payload: any, endPoint: string) => void;
  getEnableDisablePolicyRouteData: (payload: any) => void;
  getDeletePolicyRouteData: (deviceId: string, id: number, userName: string, k4Id: string) => void;
  getAllVpnProfilesData: (deviceId: string) => void;
  getPolicyRouteFlowCountsData: (deviceId: string, ruleNames: string[]) => void;
  getAllVpnProfiles: any;
  getAllPolicyRoutes: any;
  getAllPolicyRoutesLoading: boolean;
  getAddEditPolicyRoute: any;
  getAddEditPolicyRouteLoading: boolean;
  getEnableDisablePolicyRoute: any;
  getDeletePolicyRoute: any;
  getDeletePolicyRouteLoading: boolean;
  getPolicyRouteFlowCounts: any;
  getPolicyRouteFlowCountsLoading: boolean;
}

interface EgressOption {
  /** Sent as target_id. */
  id: string;
  /** Sent as target_name, and the dropdown's option value. */
  label: string;

  active: boolean;
}

/** Row shape returned by getAllPolicyRoutes. */
interface PolicyRoute {
  id: number;
  DID: string;
  name: string;
  description: string;
  target_type: 'VPN';
  target_id: string;
  target_name: string;
  source_networks: string;
  dest_ips: string[];
  enabled: boolean;
  sync_status: boolean;
}

// ============================ Reference data ============================

// dest_ips syntax is ip_or_cidr[:[proto/]port(-port)?]. Rather than document it
// in prose, offer the full matrix as pick-and-edit templates.
const IP_PORT_EXAMPLES = [
  '192.168.1.10',
  '192.168.1.10:8000',
  '192.168.1.10:8000-8999',
  '192.168.1.0/24',
  '192.168.1.0/24:8000',
  '192.168.1.0/24:8000-8999',
  '192.168.1.10:tcp',
  '192.168.1.10:tcp/8000',
  '192.168.1.10:tcp/8000-8999',
  '192.168.1.0/24:tcp',
  '192.168.1.0/24:tcp/8000',
  '192.168.1.0/24:tcp/8000-8999',
  '192.168.1.10:udp',
  '192.168.1.10:udp/8000',
  '192.168.1.10:udp/8000-8999',
  '192.168.1.0/24:udp',
  '192.168.1.0/24:udp/8000',
  '192.168.1.0/24:udp/8000-8999',
];

/** Backend cap on name length. */
const NAME_MAX_LENGTH = 28;

const REFRESH_INTERVAL_MS = 30000;

// ============================ Helpers ============================

/** Trims, drops blanks and de-duplicates a comma separated list. */
const parseCsv = (raw: string) => {
  const parts = (raw || '').split(',').map((p) => p.trim()).filter(Boolean);
  return parts.filter((p, i) => parts.indexOf(p) === i);
};

/** "Netflix, YouTube +1" — first two values with an overflow count. */
const summariseList = (values: string[]) => {
  const list = values || [];
  const shown = list.slice(0, 2).join(', ');
  return list.length > 2 ? `${shown} +${list.length - 2}` : shown;
};

/** One-line Destination cell for the list. */
const destinationSummary = (route: PolicyRoute) => summariseList(route.dest_ips);

/**
 * Source Network(s) is optional (Destination alone is a valid rule), but
 * backend rejects 0.0.0.0 as a source and only allows 0.0.0.0/0. Mirrored
 * here so the user finds out before the round trip.
 */
const validateSourceNetworks = (raw: string) => {
  const entries = parseCsv(raw);
  if (entries.length === 0) return '';
  if (entries.some((e) => e === '0.0.0.0')) return 'Use 0.0.0.0/0 to match all sources, not 0.0.0.0.';
  return '';
};

/**
 * LabelValueDisplay pins its field and inner input to a 28px single-line
 * height, which clamps a multiline field so its rows overflow past the outline.
 * Released locally here rather than in the shared component.
 */
const MULTILINE_FIX = {
  '& .MuiFormControl-root': { height: 'auto !important', lineHeight: 'normal !important' },
  '& .MuiOutlinedInput-input': { height: 'auto !important' },
} as const;

/**
 * Destination grows with MULTILINE_FIX above, but with no cap a long list of
 * IP/port entries pushes the modal taller indefinitely. Caps the textarea at
 * ~9 lines and scrolls past that, without touching the shared component.
 */
const DESTINATION_MULTILINE_FIX = {
  ...MULTILINE_FIX,
  '& .MuiOutlinedInput-input': {
    height: 'auto !important',
    maxHeight: '200px',
    overflowY: 'auto !important',
  },
} as const;

interface FormState {
  name: string;
  description: string;
  egressLabel: string;
  sourceNetworks: string;
  ipPort: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  egressLabel: '',
  sourceNetworks: '',
  ipPort: '',
};

// ============================ Main ============================

const PolicyRoutes: React.FC<Props> = ({
  selectedRow,
  refreshIconClicked,
  notifyConfigAction,
  notifyConfigActionComplete,
  getAllPolicyRoutesData,
  getAddEditPolicyRouteData,
  getEnableDisablePolicyRouteData,
  getDeletePolicyRouteData,
  getAllVpnProfilesData,
  getPolicyRouteFlowCountsData,
  getAllVpnProfiles,
  getAllPolicyRoutes,
  getAllPolicyRoutesLoading,
  getAddEditPolicyRoute,
  getAddEditPolicyRouteLoading,
  getEnableDisablePolicyRoute,
  getDeletePolicyRoute,
  getDeletePolicyRouteLoading,
  getPolicyRouteFlowCounts,
  getPolicyRouteFlowCountsLoading,
}) => {
  const dispatch = useDispatch();
  const deviceId = selectedRow?.deviceId;
  const k4Id = selectedRow?.k4Id;

  const [routes, setRoutes] = useState<PolicyRoute[]>([]);
  const [flowCounts, setFlowCounts] = useState<Record<string, number>>({});
  const [vpnProfileOptions, setVpnProfileOptions] = useState<EgressOption[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PolicyRoute | null>(null);
  const [formData, setFormData] = useState<FormState>(EMPTY_FORM);
  const [examplesAnchor, setExamplesAnchor] = useState<HTMLElement | null>(null);
  const [sourceError, setSourceError] = useState('');

  const isEditPopup = editingId !== null;

  const egressByLabel = (label: string) => vpnProfileOptions.find((o) => o.label === label);
  const egressByTarget = (route: PolicyRoute) =>
    vpnProfileOptions.find((o) => o.id === String(route.target_id));

  // A route can only egress via a tunnel the device is actually running, so
  // inactive profiles are not offered. The one exception is the value this
  // route already holds: dropping it from the list while editing would blank
  // the field and let the route be saved with no egress at all.
  const egressDropdownOptions = useMemo(
    () => vpnProfileOptions.filter((o) => o.active || o.label === formData.egressLabel).map((o) => o.label),
    [vpnProfileOptions, formData.egressLabel]
  );

  // ---- data ----

  const isRoutesBackgroundFetchRef = useRef(false);
  const routesRef = useRef<PolicyRoute[]>([]);

  const fetchRoutes = (background = false) => {
    if (!deviceId) return;
    isRoutesBackgroundFetchRef.current = background;
    getAllPolicyRoutesData(deviceId);
    getAllVpnProfilesData(deviceId);
  };

  useEffect(() => {
    fetchRoutes();
  }, [deviceId, refreshIconClicked]);

  useEffect(() => {
    if (!deviceId) return;
    const intervalId = setInterval(() => fetchRoutes(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [deviceId]);

  const isFlowCountsBackgroundFetchRef = useRef(false);

  const fetchFlowCounts = (routeList: PolicyRoute[], background = false) => {
    if (!deviceId || !routeList.length) return;
    isFlowCountsBackgroundFetchRef.current = background;
    getPolicyRouteFlowCountsData(deviceId, routeList.map((r) => r.name));
  };

  useEffect(() => {
    if (_.isEmpty(getAllPolicyRoutes)) return;
    const nextRoutes = getAllPolicyRoutes?.policy_routes ?? [];
    const wasBackground = isRoutesBackgroundFetchRef.current;
    setRoutes(nextRoutes);
    routesRef.current = nextRoutes;
    isRoutesBackgroundFetchRef.current = false;
    fetchFlowCounts(nextRoutes, wasBackground);
  }, [getAllPolicyRoutes]);

  useEffect(() => {
    if (!getPolicyRouteFlowCounts?.status) return;
    setFlowCounts(getPolicyRouteFlowCounts?.flow_counts ?? {});
    isFlowCountsBackgroundFetchRef.current = false;
  }, [getPolicyRouteFlowCounts]);

  useEffect(() => {
    if (_.isEmpty(getAllVpnProfiles)) return;
    const vpnProfiles = getAllVpnProfiles?.vpn_profiles ?? [];
    const declared = getAllVpnProfiles?.active_profiles;
    const activeIds = Array.isArray(declared) ? new Set(declared.map((id: any) => String(id))) : null;
    setVpnProfileOptions(
      vpnProfiles.map((p: any) => ({
        id: String(p.id),
        label: p.name,
        active: activeIds ? activeIds.has(String(p.id)) : !!p?.active,
      }))
    );
  }, [getAllVpnProfiles]);

  // Every write goes through syncConfigtoOE, so refetch once it lands.
  useEffect(() => {
    if (_.isEmpty(getAddEditPolicyRoute)) return;
    notifyConfigActionComplete?.();
    if (getAddEditPolicyRoute?.status) {
      handleClosePopup();
      fetchRoutes();
    }
    dispatch({ type: GET_ADD_EDIT_POLICY_ROUTE, payload: {} });
  }, [getAddEditPolicyRoute]);

  useEffect(() => {
    if (_.isEmpty(getEnableDisablePolicyRoute)) return;
    notifyConfigActionComplete?.();
    // Refetch either way: on failure the toggle must snap back to server truth.
    fetchRoutes();
    dispatch({ type: GET_ENABLE_DISABLE_POLICY_ROUTE, payload: {} });
  }, [getEnableDisablePolicyRoute]);

  useEffect(() => {
    if (_.isEmpty(getDeletePolicyRoute)) return;
    notifyConfigActionComplete?.();
    if (getDeletePolicyRoute?.status) fetchRoutes();
    dispatch({ type: GET_DELETE_POLICY_ROUTE, payload: {} });
  }, [getDeletePolicyRoute]);

  // ---- form ----

  const handleChange = (key: keyof FormState) => (e: any) => {
    const value = e.target.value;
    if (key === 'name' && String(value).length > NAME_MAX_LENGTH) return;
    if (key === 'sourceNetworks') setSourceError(validateSourceNetworks(value));
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  /** Appends a picked template to the IP & Port rules so the user can edit it. */
  const appendIpPortExample = (example: string) => {
    const current = formData.ipPort.trim();
    setFormData((prev) => ({
      ...prev,
      ipPort: current ? `${current.replace(/,\s*$/, '')}, ${example}` : example,
    }));
  };

  const handleAdd = () => {
    setFormData(EMPTY_FORM);
    setSourceError('');
    setEditingId(null);
    setIsModalOpen(true);
  };

  const handleEdit = (r: PolicyRoute) => {
    setFormData({
      name: r.name ?? '',
      description: r.description ?? '',
      egressLabel: egressByTarget(r)?.label ?? r.target_name ?? '',
      sourceNetworks: r.source_networks ?? '',
      ipPort: (r.dest_ips ?? []).join(', '),
    });
    setSourceError('');
    setEditingId(r.id);
    setIsModalOpen(true);
  };

  const handleClosePopup = () => {
    setIsModalOpen(false);
    setFormData(EMPTY_FORM);
    setSourceError('');
    setEditingId(null);
  };

  const handleSave = () => {
    const egress = egressByLabel(formData.egressLabel);
    const payload: any = {
      DID: deviceId,
      name: formData.name.trim(),
      description: formData.description.trim(),
      target_type: 'VPN',
      target_id: egress?.id,
      target_name: egress?.label,
      source_networks: parseCsv(formData.sourceNetworks).join(','),
      dest_category: 'IP_PORT',
      dest_ips: parseCsv(formData.ipPort),
      userName: getDataFromLocalStorage('loggedInEmail'),
      k4Id: k4Id,
    };

    if (isEditPopup) payload.id = editingId;

    notifyConfigAction?.();
    getAddEditPolicyRouteData(payload, isEditPopup ? 'updatePolicyRoute' : 'addPolicyRoute');
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      notifyConfigAction?.();
      getDeletePolicyRouteData(deviceId, deleteTarget.id, getDataFromLocalStorage('loggedInEmail') || '', k4Id);
    }
    setDeleteTarget(null);
  };

  const handleToggle = (row: any, checked: boolean) => {
    const route = routes.find((r) => String(r.id) === String(row?.id));
    // Backend rejects toggling to the state it is already in.
    if (!route || route.enabled === checked) return;
    notifyConfigAction?.();
    getEnableDisablePolicyRouteData({ DID: deviceId, id: route.id, enabled: checked });
  };

  const isFormValid = (): boolean => {
    const { name, egressLabel, sourceNetworks, ipPort } = formData;
    if (!name.trim() || name.trim().length > NAME_MAX_LENGTH) return false;
    if (!egressLabel || !egressByLabel(egressLabel)) return false;
    if (validateSourceNetworks(sourceNetworks)) return false;
    const hasSource = parseCsv(sourceNetworks).length > 0;
    const hasDest = parseCsv(ipPort).length > 0;
    // Source, Destination, or both must be provided — but not neither.
    if (!hasSource && !hasDest) return false;
    // Names are unique per DID; catch it here rather than on the round trip.
    return !routes.some(
      (r) => r.id !== editingId && (r.name ?? '').toLowerCase() === name.trim().toLowerCase()
    );
  };

  // ---- destination field ----

  const destPlaceholder = `e.g. ${[
    '192.168.1.10',
    '192.168.1.0/24',
    '192.168.1.10:8000',
    '192.168.1.10:8000-8999',
    '192.168.1.10:tcp/8000',
    '192.168.1.0/24:udp/8000-8999',
  ].join(', ')}`;

  const renderDestinationFields = () => (
    <Box>
      <Box
        className="label-text"
        sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6E7079', mb: '6px' }}
      >
        <span>IP &amp; Port Rules</span>
        <Box
          component="span"
          onClick={(e: React.MouseEvent<HTMLElement>) => setExamplesAnchor(e.currentTarget)}
          sx={{
            color: '#264C86',
            fontWeight: 500,
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          Examples
        </Box>
      </Box>

      <Box sx={DESTINATION_MULTILINE_FIX}>
        <LabelValueDisplay
          label=""
          isInput
          isMultiline
          textInputRows={4}
          value={formData.ipPort}
          onChange={handleChange('ipPort')}
          placeholder={destPlaceholder}
        />
      </Box>

      <Menu
        anchorEl={examplesAnchor}
        open={!!examplesAnchor}
        onClose={() => setExamplesAnchor(null)}
        PaperProps={{ sx: { maxHeight: 300 } }}
        MenuListProps={{ sx: { p: 0 } }}
      >
        {IP_PORT_EXAMPLES.map((ex) => (
          <MenuItem
            key={ex}
            onClick={() => {
              appendIpPortExample(ex);
              setExamplesAnchor(null);
            }}
            sx={{ fontFamily: 'Inter', fontSize: '13px', lineHeight: 'unset' }}
          >
            {ex}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );

  // ---- list ----

  const tableData = useMemo(
    () =>
      routes.map((r) => ({
        id: String(r.id),
        enabled: r.enabled,
        name: r.name,
        target: r.target_name || '- -',
        src: r.source_networks || 'Any',
        dest: destinationSummary(r),
        flowCount: flowCounts[r.name] !== undefined ? String(flowCounts[r.name]) : '- -',
      })),
    [routes, flowCounts]
  );

  const columns: ColumnConfig[] = [
    { label: '', key: 'enabled', type: ColumnType.sliderText, maxWidth: '56px' },
    { label: 'Policy Name', key: 'name', type: ColumnType.text, maxWidth: '170px', formatPlain: true },
    { label: 'VPN Profile', key: 'target', type: ColumnType.text, maxWidth: '200px' },
    { label: 'Source Network(s)', key: 'src', type: ColumnType.text, maxWidth: '180px' },
    { label: 'Destination', key: 'dest', type: ColumnType.text, maxWidth: '230px' },
    { label: 'No. of flows', key: 'flowCount' as any, type: ColumnType.text, maxWidth: '130px', formatPlain: true },
    { label: 'Actions', key: 'custom', type: ColumnType.actions, actions: ['setting', 'delete'] },
  ];

  const routeById = (id?: string) => routes.find((r) => String(r.id) === String(id));

  return (
    <>
      <NetworkTable
        data={tableData as any}
        columns={columns}
        showFilterBar
        addButtonLabel="Add Policy Route"
        onAddClick={handleAdd}
        onToggle={(_key: string, checked: boolean, row: any) => handleToggle(row, checked)}
        onActionClick={(type: string, _key: string, row: any) => {
          const r = routeById(row?.id);
          if (!r) return;
          if (type === 'setting') handleEdit(r);
          else if (type === 'delete') setDeleteTarget(r);
        }}
      />

      <AddEditInterfaceModal
        open={isModalOpen}
        onClose={handleClosePopup}
        onSave={handleSave}
        saveText={isEditPopup ? 'Update' : 'Add'}
        disableSave={!isFormValid() || getAddEditPolicyRouteLoading}
        title={isEditPopup ? 'Edit Policy Route' : 'Add Policy Route'}
        layout={[
          {
            columns: [
              {
                component: (
                  <LabelValueDisplay
                    label="Policy Name"
                    isInput
                    value={formData?.name}
                    onChange={handleChange('name')}
                    placeholder="Enter Policy Name"
                  />
                ),
                gridSize: 6,
              },
              {
                component: (
                  <LabelValueDisplay
                    label="VPN Profile"
                    isDropdown
                    value={formData?.egressLabel}
                    dropdownOptions={egressDropdownOptions}
                    onChange={handleChange('egressLabel')}
                    placeholder="Select VPN Profile"
                    error={
                      egressDropdownOptions.length === 0
                        ? 'No active VPN profiles. Activate one on the VPN tab first.'
                        : ''
                    }
                  />
                ),
                gridSize: 6,
              },
            ],
          },
          {
            columns: [
              {
                component: (
                  <Box sx={MULTILINE_FIX}>
                    <LabelValueDisplay
                      label="Description"
                      isInput
                      isMultiline
                      textInputRows={2}
                      value={formData?.description}
                      onChange={handleChange('description')}
                      placeholder="Enter Description"
                    />
                  </Box>
                ),
                gridSize: 12,
              },
            ],
          },
          {
            columns: [
              {
                component: (
                  <Box>
                    <LabelValueDisplay
                      label="Source Network(s)"
                      isInput
                      value={formData?.sourceNetworks}
                      onChange={handleChange('sourceNetworks')}
                      placeholder="Enter IP/Subnet e.g. 192.168.1.10, 192.168.1.0/24"
                      error={sourceError}
                    />
                    <Box sx={{ fontSize: '11px', color: '#6E7079', mt: '5px' }}>
                      Source Network(s), Destination, or both must be provided.
                    </Box>
                  </Box>
                ),
                gridSize: 12,
              },
            ],
          },
          {
            columns: [
              {
                component: (
                  <NetworkCardContainer
                    title="Destination"
                    borderGradient="linear-gradient(90deg, #597DFF 0%, rgba(53, 75, 153, 0) 100%)"
                  >
                    {renderDestinationFields()}
                  </NetworkCardContainer>
                ),
                gridSize: 12,
              },
            ],
          },
        ]}
      />

      <DeleteWANProfileModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Alert"
        titleWithDeleteIcon={true}
        confirmText="Delete"
        cancelText="Cancel"
        entityLabel={`Policy route "${deleteTarget?.name}" will be deleted.`}
        entityName={undefined}
        warningMessage="Traffic matched by this policy route will fall back to the default egress path."
      />

      {(getAllPolicyRoutesLoading && !isRoutesBackgroundFetchRef.current) ||
      getAddEditPolicyRouteLoading ||
      getDeletePolicyRouteLoading ||
      (getPolicyRouteFlowCountsLoading && !isFlowCountsBackgroundFetchRef.current) ? (
        <CustomLoader showLoader={true} loadingText={'Please wait...'} />
      ) : null}
    </>
  );
};

const mapStateToProps = (state: any) => ({
  getAllPolicyRoutes: state.authReducer.getAllPolicyRoutes,
  getAllPolicyRoutesLoading: state.authReducer.getAllPolicyRoutesLoading,
  getAddEditPolicyRoute: state.authReducer.getAddEditPolicyRoute,
  getAddEditPolicyRouteLoading: state.authReducer.getAddEditPolicyRouteLoading,
  getEnableDisablePolicyRoute: state.authReducer.getEnableDisablePolicyRoute,
  getDeletePolicyRoute: state.authReducer.getDeletePolicyRoute,
  getDeletePolicyRouteLoading: state.authReducer.getDeletePolicyRouteLoading,
  getAllVpnProfiles: state.authReducer.getAllVpnProfiles,
  getPolicyRouteFlowCounts: state.authReducer.getPolicyRouteFlowCounts,
  getPolicyRouteFlowCountsLoading: state.authReducer.getPolicyRouteFlowCountsLoading,
});

export default connect(mapStateToProps, {
  getAllPolicyRoutesData,
  getAddEditPolicyRouteData,
  getEnableDisablePolicyRouteData,
  getDeletePolicyRouteData,
  getAllVpnProfilesData,
  getPolicyRouteFlowCountsData,
})(PolicyRoutes);
